import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { validateResponse } from "@/api/validateResponse";
import {
  PractitionerBookingListResponseSchema,
  PractitionerBookingSchema,
  PractitionerCommitmentSchema,
  PractitionerListResponseSchema,
  PractitionerSchema,
  type CreateCommitmentInput,
  type CreatePractitionerBookingInput,
  type CreatePractitionerInput,
  type Practitioner,
  type PractitionerBooking,
  type PractitionerCommitment,
  type UpdatePractitionerInput,
} from "@/api/types/practitioner";
import { InvoiceSchema, type Invoice } from "@/api/types/invoice";
import { z } from "zod";

/** Query-key factory — mirrors src/api/hooks/useMemberships.ts's convention. */
export const practitionerKeys = {
  all: ["practitioners"] as const,
  lists: () => [...practitionerKeys.all, "list"] as const,
  list: (activeOnly: boolean) => [...practitionerKeys.lists(), activeOnly] as const,
  details: () => [...practitionerKeys.all, "detail"] as const,
  detail: (id: string | undefined) => [...practitionerKeys.details(), id] as const,
  commitments: (id: string | undefined) => [...practitionerKeys.all, "commitments", id] as const,
  bookings: (id: string | undefined) => [...practitionerKeys.all, "bookings", id] as const,
};

function invalidateAll(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: practitionerKeys.all });
}

export function usePractitioners(activeOnly = false) {
  return useQuery({
    queryKey: practitionerKeys.list(activeOnly),
    queryFn: async () => {
      const { data } = await apiClient.get(`/v2/practitioners?active_only=${activeOnly}`);
      return validateResponse(PractitionerListResponseSchema, data, "GET /v2/practitioners").items;
    },
    staleTime: 10 * 1000,
  });
}

export function usePractitioner(practitionerId: string | undefined) {
  return useQuery<Practitioner>({
    queryKey: practitionerKeys.detail(practitionerId),
    queryFn: async () => {
      const { data } = await apiClient.get(`/v2/practitioners/${practitionerId}`);
      return validateResponse(PractitionerSchema, data, "GET /v2/practitioners/:id");
    },
    enabled: !!practitionerId,
  });
}

export function useCreatePractitioner() {
  const queryClient = useQueryClient();
  return useMutation<Practitioner, unknown, CreatePractitionerInput>({
    mutationFn: async (input) => {
      const { data } = await apiClient.post("/v2/practitioners", input);
      return validateResponse(PractitionerSchema, data, "POST /v2/practitioners");
    },
    onSuccess: (practitioner) => {
      queryClient.setQueryData(practitionerKeys.detail(practitioner.id), practitioner);
      invalidateAll(queryClient);
    },
  });
}

export function useUpdatePractitioner() {
  const queryClient = useQueryClient();
  return useMutation<Practitioner, unknown, UpdatePractitionerInput>({
    mutationFn: async ({ id, ...patch }) => {
      const { data } = await apiClient.patch(`/v2/practitioners/${id}`, patch);
      return validateResponse(PractitionerSchema, data, "PATCH /v2/practitioners/:id");
    },
    onSuccess: (practitioner) => {
      queryClient.setQueryData(practitionerKeys.detail(practitioner.id), practitioner);
      invalidateAll(queryClient);
    },
  });
}

export function usePractitionerCommitments(practitionerId: string | undefined) {
  return useQuery<PractitionerCommitment[]>({
    queryKey: practitionerKeys.commitments(practitionerId),
    queryFn: async () => {
      const { data } = await apiClient.get(`/v2/practitioners/${practitionerId}/commitments`);
      return validateResponse(z.array(PractitionerCommitmentSchema), data, "GET /v2/practitioners/:id/commitments");
    },
    enabled: !!practitionerId,
    staleTime: 10 * 1000,
  });
}

export function useCreateCommitment() {
  const queryClient = useQueryClient();
  return useMutation<PractitionerCommitment, unknown, CreateCommitmentInput>({
    mutationFn: async ({ practitionerId, start_month }) => {
      const { data } = await apiClient.post(`/v2/practitioners/${practitionerId}/commitments`, { start_month });
      return validateResponse(PractitionerCommitmentSchema, data, "POST /v2/practitioners/:id/commitments");
    },
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({ queryKey: practitionerKeys.commitments(variables.practitionerId) });
    },
  });
}

export function usePractitionerBookings(practitionerId: string | undefined) {
  return useQuery<PractitionerBooking[]>({
    queryKey: practitionerKeys.bookings(practitionerId),
    queryFn: async () => {
      const { data } = await apiClient.get(`/v2/practitioners/${practitionerId}/bookings`);
      return validateResponse(PractitionerBookingListResponseSchema, data, "GET /v2/practitioners/:id/bookings").items;
    },
    enabled: !!practitionerId,
    staleTime: 10 * 1000,
  });
}

export function useCreatePractitionerBooking() {
  const queryClient = useQueryClient();
  return useMutation<PractitionerBooking, unknown, CreatePractitionerBookingInput>({
    mutationFn: async ({ practitionerId, ...input }) => {
      const { data } = await apiClient.post(`/v2/practitioners/${practitionerId}/bookings`, input);
      return validateResponse(PractitionerBookingSchema, data, "POST /v2/practitioners/:id/bookings");
    },
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({ queryKey: practitionerKeys.bookings(variables.practitionerId) });
    },
  });
}

function usePractitionerBookingAction(endpoint: string, label: string) {
  const queryClient = useQueryClient();
  return useMutation<PractitionerBooking, unknown, string>({
    mutationFn: async (practitionerBookingId) => {
      const { data } = await apiClient.post(`/v2/practitioners/bookings/${practitionerBookingId}/${endpoint}`, {});
      return validateResponse(PractitionerBookingSchema, data, `POST /v2/practitioners/bookings/:id/${label}`);
    },
    onSuccess: (booking) => {
      void queryClient.invalidateQueries({ queryKey: practitionerKeys.bookings(booking.practitioner_id) });
    },
  });
}

export function useConfirmPractitionerBooking() {
  return usePractitionerBookingAction("confirm", "confirm");
}

export function useCancelPractitionerBooking() {
  return usePractitionerBookingAction("cancel", "cancel");
}

export function useCompletePractitionerBooking() {
  return usePractitionerBookingAction("complete", "complete");
}

export function useGeneratePractitionerInvoice() {
  const queryClient = useQueryClient();
  return useMutation<Invoice, unknown, { practitionerBookingId: string; practitionerId: string }>({
    mutationFn: async ({ practitionerBookingId }) => {
      const { data } = await apiClient.post(`/v2/practitioners/bookings/${practitionerBookingId}/generate-invoice`, {});
      return validateResponse(InvoiceSchema, data, "POST /v2/practitioners/bookings/:id/generate-invoice");
    },
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({ queryKey: practitionerKeys.bookings(variables.practitionerId) });
      void queryClient.invalidateQueries({ queryKey: ["invoices"] });
    },
  });
}
